import {
  ETAIEMENT_SCOPE_MATRIX,
  ETAIEMENT_SCOPE_MATRIX_ROW_IDS,
} from '~/constant/etaiements/phases/scope-matrix';

export interface IEtaiementScopeMatrixRow {
  id: string;
  lvtec: boolean;
  eiffage: boolean;
}

export interface IEtaiementScopeMatrix {
  rows: IEtaiementScopeMatrixRow[];
}

const VALID_ROW_IDS = new Set(ETAIEMENT_SCOPE_MATRIX_ROW_IDS);

export const buildDefaultScopeMatrix = (): IEtaiementScopeMatrix => ({
  rows: ETAIEMENT_SCOPE_MATRIX_ROW_IDS.map((id) => ({
    id,
    lvtec: false,
    eiffage: false,
  })),
});

export const normalizeScopeMatrix = (
  saved?: IEtaiementScopeMatrix | null,
): IEtaiementScopeMatrix => {
  const defaults = buildDefaultScopeMatrix();
  if (!saved?.rows?.length) {
    return defaults;
  }

  const savedById = new Map(
    saved.rows
      .filter((row) => VALID_ROW_IDS.has(row.id))
      .map((row) => [row.id, row]),
  );

  return {
    rows: defaults.rows.map((defaultRow) => {
      const persisted = savedById.get(defaultRow.id);
      if (!persisted) return defaultRow;
      return {
        id: defaultRow.id,
        lvtec: Boolean(persisted.lvtec),
        eiffage: Boolean(persisted.eiffage),
      };
    }),
  };
};

export const sanitizeScopeMatrixForPersistence = (
  rows: IEtaiementScopeMatrixRow[] | null | undefined,
): IEtaiementScopeMatrixRow[] => (
  normalizeScopeMatrix({ rows: rows || [] }).rows
);

const hasPersistedScopeMatrix = (
  saved?: IEtaiementScopeMatrix | null,
): boolean => Boolean(saved?.rows?.length);

export const mergeScopeMatrices = (
  matrices: Array<IEtaiementScopeMatrix | null | undefined>,
): IEtaiementScopeMatrix => {
  const merged = buildDefaultScopeMatrix();
  const mergedById = new Map(merged.rows.map((row) => [row.id, { ...row }]));

  matrices.forEach((matrix) => {
    const normalized = normalizeScopeMatrix(matrix);
    normalized.rows.forEach((row) => {
      const current = mergedById.get(row.id);
      if (!current) return;
      current.lvtec = current.lvtec || row.lvtec;
      current.eiffage = current.eiffage || row.eiffage;
    });
  });

  return {
    rows: merged.rows.map((row) => mergedById.get(row.id) || row),
  };
};

const getPhaseScopeMatrix = (phase: unknown): IEtaiementScopeMatrix | null | undefined => {
  if (!phase || typeof phase !== 'object' || !('scopeMatrix' in phase)) {
    return undefined;
  }
  return (phase as { scopeMatrix?: IEtaiementScopeMatrix | null }).scopeMatrix;
};

export const resolveEtaiementScopeMatrix = (etaiement?: {
  scopeMatrix?: IEtaiementScopeMatrix | null;
  phases?: ReadonlyArray<unknown> | null;
} | null): IEtaiementScopeMatrix => {
  if (hasPersistedScopeMatrix(etaiement?.scopeMatrix)) {
    return normalizeScopeMatrix(etaiement?.scopeMatrix);
  }
  return mergeScopeMatrices((etaiement?.phases || []).map(getPhaseScopeMatrix));
};

export type ScopeMatrixRowWithMeta = IEtaiementScopeMatrixRow & {
  label: string;
  sectionId: string;
  sectionLabel: string;
};

export const getScopeMatrixRowsWithMeta = (
  saved?: IEtaiementScopeMatrix | null,
): ScopeMatrixRowWithMeta[] => {
  const normalized = normalizeScopeMatrix(saved);
  const rowsById = new Map(normalized.rows.map((row) => [row.id, row]));

  return ETAIEMENT_SCOPE_MATRIX.sections.flatMap((section) => (
    section.rows.map((rowDef) => {
      const row = rowsById.get(rowDef.id) || { id: rowDef.id, lvtec: false, eiffage: false };
      return {
        ...row,
        label: rowDef.label,
        sectionId: section.id,
        sectionLabel: section.label,
      };
    })
  ));
};
